import React, { forwardRef } from 'react'
import { keyframes } from '@emotion/react'

import Box, { type BoxProps } from '../box'
import { accessibilityLabel } from '../_support'
import type { ThemeColor } from '@theme'

/* -----------------------------------------------------------------------------
 * Loading
 *
 * Two dots turning around their middle, 750ms a turn with a quad ease in and
 * out, as the Animated loop of the @walrus/common kit drew them.
 * -------------------------------------------------------------------------- */

const sizes = {
  small: 8,
  medium: 12,
  large: 16
} as const

const spin = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' }
})

// Easing.inOut(Easing.quad)
const EASE_IN_OUT_QUAD = 'cubic-bezier(0.455, 0.03, 0.515, 0.955)'

export type LoadingProps = BoxProps & {
  size?: keyof typeof sizes
  fill?: ThemeColor
}

const Dot = ({
  size,
  style,
  bg
}: {
  size: number
  style?: object
  bg: ThemeColor
}) => (
  <Box
    bg={bg}
    style={[{ width: size, height: size, borderRadius: size }, style]}
  />
)

const Loading = forwardRef<HTMLElement, LoadingProps>(
  ({ fill = 'violet', size = 'medium', ...rest }, ref) => {
    const dot = sizes[size]
    return (
      <Box
        ref={ref}
        {...accessibilityLabel('Loading')}
        alignSelf='center'
        {...rest}
      >
        <Box
          flexDirection='row'
          position='absolute'
          top={-dot / 2}
          left={-(dot * 3) / 2}
          style={{ animation: `${spin} 750ms ${EASE_IN_OUT_QUAD} infinite` }}
        >
          <Dot size={dot} style={{ marginRight: dot }} bg={fill} />
          <Dot size={dot} bg={fill} />
        </Box>
      </Box>
    )
  }
)
Loading.displayName = 'Loading'

export default Loading
